import { ElementType, FC, ReactNode, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { FontColorType, Text } from '@frontend/design-system';
import { useOnboardingTaskAction } from '../../hooks/use-onboarding-task-action';
import { useOnboardingModuleTasks } from '../../provider/onboarding-module-tasks.provider';
import { getOnboardingTaskTrackingId, TaskCardTrackingIds } from '../../tracking-ids';
import { OnboardingActionSource } from '../../types';
import {
  formatAppointmentDate,
  getBlockingTasks,
  getTaskScheduleDates,
  getTaskStatusIcon,
  isTaskBlocked,
  isTaskCompleted,
} from '../../utils';

interface TaskItemProps {
  task: OnboardingTasksV2.Types.TaskRow;
  renderIcon?: ReactNode;
  containerStyles?: Interpolation<Theme>;
  source?: OnboardingActionSource;
  as?: ElementType;
}

export const TaskItem: FC<TaskItemProps> = ({
  containerStyles,
  renderIcon,
  task,
  source = 'onboarding-dashboard-page',
  as: Component = 'li',
}) => {
  const { onboardingModuleTasks, selectedLocationId } = useOnboardingModuleTasks();

  const { renderTaskActionModals, isActionableTask, disableAction, handleTaskClick } = useOnboardingTaskAction({
    task,
    locationId: selectedLocationId,
  });

  const { t } = useTranslation('onboardingDashboard');
  const { icon, color } = getTaskStatusIcon(task, onboardingModuleTasks);

  const { blockingTasks, isBlocked } = useMemo(() => {
    return {
      isBlocked: isTaskBlocked(task, onboardingModuleTasks),
      blockingTasks: getBlockingTasks(task, onboardingModuleTasks),
    };
  }, [task, onboardingModuleTasks]);

  const { titleColor, subTitleColor, subTitle } = useMemo<{
    titleColor: FontColorType;
    subTitleColor: FontColorType;
    subTitle?: string;
  }>(() => {
    const scheduledDate = getTaskScheduleDates(task)?.[0];

    const subTitle = scheduledDate
      ? formatAppointmentDate(scheduledDate)
      : isBlocked
      ? t('{{taskName}} first', { taskName: blockingTasks[0]?.taskTitle })
      : '';
    return {
      titleColor: isBlocked ? 'disabled' : 'default',
      subTitleColor: isBlocked ? 'primary' : 'subdued',
      subTitle,
    };
  }, [blockingTasks, isBlocked, task, t]);

  const taskCompleted = isTaskCompleted(task);

  const showTaskAction = isActionableTask && !disableAction && !isBlocked;
  return (
    <>
      <Component
        css={[styles.container, containerStyles, taskCompleted && styles.disabled, showTaskAction && styles.clickable]}
        data-trackingid={getOnboardingTaskTrackingId({
          taskName: task.taskName,
          trackingId: TaskCardTrackingIds.click,
          source,
        })}
        onClick={showTaskAction ? handleTaskClick : undefined}
      >
        {renderIcon || <Icon name={icon} size={24} css={{ color }} />}
        <div css={styles.body}>
          <Text size='large' color={titleColor}>
            {task.taskTitle}
          </Text>
          {!!subTitle && (
            <Text size='medium' color={subTitleColor}>
              {subTitle}
            </Text>
          )}
        </div>
        {showTaskAction && <Icon name='caret-right-small' color='subdued' />}
      </Component>
      {renderTaskActionModals}
    </>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(1)};
  `,
  disabled: css`
    opacity: 0.5;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    flex: 1;
  `,
  clickable: css`
    cursor: pointer;
    &:hover {
      background-color: ${theme.colors.neutral5};
    }
  `,
};
